import React from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Autocomplete,
} from "@mui/material";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import dayjs from "dayjs";
import { applyLeave } from "../../services/leaveService";
import BasicModal from "../../components/BasicModal";
import FormModalActions from "../../components/FormModalActions";
import {
  DENSE_FIELD_PROPS,
  DENSE_FORM_BODY_SX,
  FORM_MODAL_CONTENT_SX,
} from "../../utils/formModalStyles";
import { useSelector } from 'react-redux'; // Import useSelector
import { getDoctorByUserId } from "../../services/doctorService"; // Import the service
import { getStaffByUserId } from "../../services/StaffService"; // Import the service

const today = dayjs();

const validationSchema = Yup.object({
  fromDate: Yup.object().nullable().required("From Date is required"),
  toDate: Yup.object().nullable().required("To Date is required"),
  leaveType: Yup.string().required("Leave Type is required"),
  leaveReason: Yup.string().required("Reason is required"),
});

const AddNewInvoice = ({ open, onClose, invoice_info }) => {
  const user = useSelector((state) => state.user.user); // Access user from Redux
  const formik = useFormik({
    initialValues: {
      fromDate: invoice_info?.fromDate ? dayjs(invoice_info.fromDate) : null,
      toDate: invoice_info?.toDate ? dayjs(invoice_info.toDate) : null,
      leaveType: invoice_info?.leaveType || "",
      leaveReason: invoice_info?.leaveReason || "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      // Frontend validation: fromDate should NOT be after toDate
      if (values.fromDate && values.toDate && values.fromDate.isAfter(values.toDate)) {
        formik.setFieldError("fromDate", "From Date cannot be after To Date");
        return; // stop form submission
      }

      try {
        // Fetch user details based on user.id
        let userDetails;
        if (user.roleName === "doctor" || user.roleName === "surgeon") {
          userDetails = await getDoctorByUserId(user.id); // Fetch doctor details
        } else {
          userDetails = await getStaffByUserId(user.id); // Fetch staff details
        }

        const payload = {
          attendanceDate: values.fromDate ? values.fromDate.format("YYYY-MM-DD") : null, // Set attendance date
          leaveFromDate: values.fromDate ? values.fromDate.format("YYYY-MM-DD") : null, // Map to leaveFromDate
          leaveToDate: values.toDate ? values.toDate.format("YYYY-MM-DD") : null, // Map to leaveToDate
          leaveType: values.leaveType,
          leaveReason: values.leaveReason,
          // userId: user?.id || null, // yahan se userId lo
          ...(user?.roleName === 'doctor' 
            ? { doctorId: userDetails.id } // Use fetched doctor ID
            : { staffId: userDetails.id }  // Use fetched staff ID
          )
        };

        await applyLeave(payload);
        alert("Leave applied successfully!");  // Correctly sending formatted payload
        handelCloseModalState();
      } catch (error) {
        console.error("Apply leave error:", error);
        alert("Failed to apply for leave.");
      }
    },
  });

  const handelCloseModalState = () => {
    onClose();
    formik.resetForm();
  };

  return (
    <BasicModal
      open={open}
      onClose={handelCloseModalState}
      title="Apply Leave"
    >
      <Box sx={{ ...FORM_MODAL_CONTENT_SX, ...DENSE_FORM_BODY_SX }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={1}>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From Date"
                    format="YYYY-MM-DD"
                    value={formik.values.fromDate}
                    onChange={(value) => formik.setFieldValue("fromDate", value)}
                    minDate={today} 
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: formik.touched.fromDate && Boolean(formik.errors.fromDate),
                        helperText: formik.touched.fromDate && formik.errors.fromDate,
                        ...DENSE_FIELD_PROPS,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="To Date"
                    format="YYYY-MM-DD"
                    value={formik.values.toDate}
                    onChange={(value) => formik.setFieldValue("toDate", value)}
                    minDate={today}  // disable past dates
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: formik.touched.toDate && Boolean(formik.errors.toDate),
                        helperText: formik.touched.toDate && formik.errors.toDate,
                        ...DENSE_FIELD_PROPS,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={[
                    "Casual Leave (CL)",
                    "Sick Leave (SL)",
                    "Earned Leave / Privilege Leave (EL/PL)",
                    "Maternity Leave",
                    "Paternity Leave",
                    "Marriage Leave",
                    "Unpaid Leave / Leave Without Pay (LWP)",
                    "Sabbatical Leave",
                  ]}
                  value={formik.values.leaveType}
                  onChange={(event, newValue) => {
                    formik.setFieldValue("leaveType", newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Leave Type"
                      error={formik.touched.leaveType && Boolean(formik.errors.leaveType)}
                      helperText={formik.touched.leaveType && formik.errors.leaveType}
                      {...DENSE_FIELD_PROPS}
                    />
                  )}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  id="leaveReason"
                  name="leaveReason"
                  label="Leave Reason"
                  value={formik.values.leaveReason}
                  onChange={formik.handleChange}
                  error={formik.touched.leaveReason && Boolean(formik.errors.leaveReason)}
                  helperText={formik.touched.leaveReason && formik.errors.leaveReason}
                  {...DENSE_FIELD_PROPS}
                />
              </Grid>

              <Grid item xs={12}>
                <FormModalActions
                  onCancel={handelCloseModalState}
                  submitType="submit"
                  submitDisabled={formik.isSubmitting}
                  loading={formik.isSubmitting}
                />
              </Grid>

          </Grid>
        </form>
      </Box>
    </BasicModal>
  );
};

export default AddNewInvoice;